'use client';

import type { IconProps } from '@shared/icons/types';
import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';
import type { LotteryName } from '../../lottery-purchases/types';
import { LoadingTippengineSkeleton } from '../components/LoadingTippengineSkeleton/LoadingTippengineSkeleton';
import type { LotteryConfig } from '../types';
import type { LotteryTicketProps } from './lotteries.types';

type LotteryTicket = ComponentType<Pick<LotteryTicketProps, 'lotteryConfig' | 'icon'>>;

type ClientLotteryProps = {
  lotteryName: LotteryName;
  lotteryConfig: LotteryConfig;
  icon?: ComponentType<IconProps>;
};

function clientLottery(lotteryName: LotteryName, load: () => Promise<{ default: LotteryTicket }>) {
  return dynamic(load, {
    ssr: false,
    loading: () => <LoadingTippengineSkeleton lotteryName={lotteryName} />,
  });
}

const LOTTERIES: Partial<Record<LotteryName, LotteryTicket>> = {
  irishLotto: clientLottery('irishLotto', () => import('./irishLotto').then(module => ({ default: module.IrishLotto }))),
  powerBall: clientLottery('powerBall', () => import('./powerBall').then(module => ({ default: module.PowerBall }))),
  euroJackpot: clientLottery('euroJackpot', () => import('./euroJackpot').then(module => ({ default: module.EuroJackpot }))),
  euroDreams: clientLottery('euroDreams', () => import('./euroDreams').then(module => ({ default: module.EuroDreams }))),
  megaMillions: clientLottery('megaMillions', () => import('./megamillions').then(module => ({ default: module.MegaMillions }))),
  polishMiniLotto: clientLottery('polishMiniLotto', () => import('./miniLotto').then(module => ({ default: module.MiniLotto }))),
  superEnalotto: clientLottery('superEnalotto', () => import('./superEnalotto').then(module => ({ default: module.SuperEnalotto }))),
  lotto5: clientLottery('lotto5', () => import('./lotto5').then(module => ({ default: module.Lotto5 }))),
  euroMillionsPlus: clientLottery('euroMillionsPlus', () => import('./euroMillionsPlus').then(module => ({ default: module.EuroMillionsPlus }))),
  euroMillionsGo: clientLottery('euroMillionsGo', () => import('./euroMillionsGo').then(module => ({ default: module.EuroMillionsGo }))),
  luckyForLife: clientLottery('luckyForLife', () => import('./luckyForLife').then(module => ({ default: module.LuckyForLife }))),
  theDailyMillion: clientLottery('theDailyMillion', () => import('./theDailyMillion').then(module => ({ default: module.DailyMillion }))),
};

function ClientLottery({ lotteryName, lotteryConfig, icon }: ClientLotteryProps) {
  const Lottery = LOTTERIES[lotteryName];

  if (!Lottery) {
    return null;
  }

  return <Lottery lotteryConfig={lotteryConfig} icon={icon} />;
}

export { ClientLottery };
export type { ClientLotteryProps };
